'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';

interface PaymentMethodsSectionProps {
    // Chargebee handlers
    googlePayHandler: unknown | null;
    applePayHandler: unknown | null;

    // Payment intents
    googlePayIntent: unknown | null;
    applePayIntent: unknown | null;

    // State
    isLoading: boolean;
    hasTrial: boolean;
    trialDays: number;
    trialEndDate: Date | null;
    amount: number;
    isAppleDevice: boolean;
    isYearly: boolean;
    planName: string;

    // User info validation (for card form submit button)
    isUserInfoComplete?: boolean;

    // Dark mode support
    isDarkMode?: boolean;

    // Callbacks
    onGooglePaySuccess: (
        token: string,
        gatewayAccountId: string,
        billingInfo?: WalletBillingInfo
    ) => void;
    onApplePaySuccess: (
        token: string,
        gatewayAccountId: string,
        billingInfo?: WalletBillingInfo
    ) => void;
    onCardSubmit: (cardData: CardData) => void;
    onError: (error: string) => void;
    onCardFormToggle?: (isActive: boolean) => void;
    onWalletsReady?: () => void; // Called when all wallet buttons are mounted
    // Called when Google Pay SDK hangs (user returned but callback not received)
    // Parent should verify payment status with backend
    onGooglePayTimeout?: () => Promise<boolean>; // Returns true if payment verified successful
}

export interface CardData {
    number: string;
    expiryMonth: string;
    expiryYear: string;
    cvv: string;
}

export interface WalletBillingInfo {
    firstName?: string;
    lastName?: string;
    email?: string;
}

/**
 * Payment methods section with digital wallets and collapsible card input
 * - Apple devices: Show Apple Pay
 * - Non-Apple devices: Show Google Pay
 * - Credit card is hidden by default, revealed on user request
 */
export function PaymentMethodsSection({
    googlePayHandler,
    applePayHandler,
    googlePayIntent,
    applePayIntent,
    isLoading,
    hasTrial,
    trialDays,
    trialEndDate,
    amount,
    isAppleDevice,
    isYearly,
    planName,
    isUserInfoComplete = true,
    isDarkMode = true,
    onGooglePaySuccess,
    onApplePaySuccess,
    onCardSubmit,
    onError,
    onCardFormToggle,
    onWalletsReady,
    onGooglePayTimeout,
}: PaymentMethodsSectionProps) {
    // Feature flags for payment method visibility
    // Default to true so buttons show unless explicitly disabled in PostHog
    const googlePayFeatureEnabled = useFeatureFlag('google_pay_enabled', true);
    const applePayFeatureEnabled = useFeatureFlag('apple_pay_enabled', true);

    // Debug: Log feature flag values
    useEffect(() => {
        console.log('[PAYMENT-METHODS] Feature flag values:', {
            googlePayFeatureEnabled,
            applePayFeatureEnabled,
            timestamp: new Date().toISOString(),
        });
    }, [googlePayFeatureEnabled, applePayFeatureEnabled]);

    const [googlePayMounted, setGooglePayMounted] = useState(false);
    const [applePayMounted, setApplePayMounted] = useState(false);
    // Track mount failures so we don't get stuck waiting
    const [googlePayFailed, setGooglePayFailed] = useState(false);
    const [applePayFailed, setApplePayFailed] = useState(false);
    const [walletPaymentInProgress, setWalletPaymentInProgress] =
        useState(false);
    const [showCardForm, setShowCardForm] = useState(false);

    // Track if initial wallet loading is complete (to avoid UI flicker)
    const [walletsInitialized, setWalletsInitialized] = useState(false);
    // Track if ALL expected wallet buttons are mounted (for unified appearance)
    const [allWalletsMounted, setAllWalletsMounted] = useState(false);

    // Card form state
    const [cardNumber, setCardNumber] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [cvv, setCvv] = useState('');
    const [cardErrors, setCardErrors] = useState<{
        number?: string;
        expiry?: string;
        cvv?: string;
    }>({});
    const [cardBrand, setCardBrand] = useState<string | null>(null);

    const googlePayRef = useRef<HTMLDivElement>(null);
    const applePayRef = useRef<HTMLDivElement>(null);

    // Track when a remount is pending to prevent race conditions
    const googlePayRemountPendingRef = useRef(false);
    const applePayRemountPendingRef = useRef(false);
    // Counter to force re-trigger of mount effects after remount sequence
    const [mountTrigger, setMountTrigger] = useState(0);

    const showDigitalWallets = amount > 0;

    // Web app pattern: Show wallets unconditionally based on handler presence
    // Don't check for specific methods - let Chargebee handle compatibility
    // The web app sets googlePaySupported = true ALWAYS, applePaySupported = true on Apple devices

    // Debug: log handler info and feature flags
    useEffect(() => {
        console.log(
            '[PAYMENT] Handler info (web app pattern - no method validation):',
            {
                googlePayHandler: !!googlePayHandler,
                googlePayHandlerMethods: googlePayHandler
                    ? Object.keys(googlePayHandler as object)
                    : [],
                applePayHandler: !!applePayHandler,
                applePayHandlerMethods: applePayHandler
                    ? Object.keys(applePayHandler as object)
                    : [],
                isAppleDevice,
                featureFlags: {
                    googlePayFeatureEnabled,
                    applePayFeatureEnabled,
                },
            }
        );
    }, [
        googlePayHandler,
        applePayHandler,
        isAppleDevice,
        googlePayFeatureEnabled,
        applePayFeatureEnabled,
    ]);

    // Web app pattern: Show wallets based on handler presence, not method validation
    // - Google Pay: Show if handler exists AND feature flag is enabled
    // - Apple Pay: Show on Apple devices if handler exists AND feature flag is enabled
    const showApplePay =
        isAppleDevice && !!applePayHandler && applePayFeatureEnabled;
    const showGooglePay = !!googlePayHandler && googlePayFeatureEnabled;

    // Check if we have any working wallet (not failed)
    const hasWorkingGooglePay = showGooglePay && !googlePayFailed;
    const hasWorkingApplePay = showApplePay && !applePayFailed;
    const hasWallet = hasWorkingGooglePay || hasWorkingApplePay;

    // Notify parent when card form visibility changes or when card is the only option
    useEffect(() => {
        // Only notify after wallets are initialized to prevent flicker
        if (!walletsInitialized) return;
        const cardIsActive = showCardForm || !hasWallet || amount <= 0;
        onCardFormToggle?.(cardIsActive);
    }, [showCardForm, hasWallet, amount, onCardFormToggle, walletsInitialized]);

    // Mark wallets as initialized once loading is complete (regardless of whether wallets are available)
    useEffect(() => {
        if (!isLoading) {
            setWalletsInitialized(true);
        }
    }, [isLoading]);

    // Reset mounted state when intents change (e.g., after coupon apply/remove)
    // This allows the wallet buttons to remount with updated trial information
    const prevGooglePayIntentRef = useRef(googlePayIntent);
    const prevApplePayIntentRef = useRef(applePayIntent);
    useEffect(() => {
        const prevGooglePayIntent = prevGooglePayIntentRef.current;
        const prevApplePayIntent = prevApplePayIntentRef.current;

        // Check if intents have changed from a truthy value (was mounted, now needs remount)
        // We check if PREVIOUS intent was truthy because recreateIntents() clears first then creates new
        const googlePayIntentCleared = prevGooglePayIntent && !googlePayIntent;
        const applePayIntentCleared = prevApplePayIntent && !applePayIntent;
        const googlePayIntentReplaced =
            prevGooglePayIntent &&
            googlePayIntent &&
            googlePayIntent !== prevGooglePayIntent;
        const applePayIntentReplaced =
            prevApplePayIntent &&
            applePayIntent &&
            applePayIntent !== prevApplePayIntent;

        // If intent was cleared or replaced and we were already mounted, reset to allow remount
        // Use remountPending flag to prevent mount effect from running during transition
        if (
            (googlePayIntentCleared || googlePayIntentReplaced) &&
            googlePayMounted
        ) {
            console.log(
                '[PAYMENT] Google Pay intent changed, starting remount sequence',
                {
                    cleared: googlePayIntentCleared,
                    replaced: googlePayIntentReplaced,
                }
            );
            // Set remount pending BEFORE clearing to prevent race conditions
            googlePayRemountPendingRef.current = true;
            // Clear the existing button container before remounting
            if (googlePayRef.current) {
                googlePayRef.current.innerHTML = '';
            }
            // Reset mounted state immediately for cleared intents, with delay for replaced
            setGooglePayMounted(false);
            setAllWalletsMounted(false);
            // Clear pending flag after a delay to allow new intent to arrive
            setTimeout(() => {
                googlePayRemountPendingRef.current = false;
                console.log(
                    '[PAYMENT] Google Pay remount sequence complete, ready for new mount'
                );
                // Increment trigger to force mount effect to re-run
                setMountTrigger((prev) => prev + 1);
            }, 100);
        }
        if (
            (applePayIntentCleared || applePayIntentReplaced) &&
            applePayMounted
        ) {
            console.log(
                '[PAYMENT] Apple Pay intent changed, starting remount sequence',
                {
                    cleared: applePayIntentCleared,
                    replaced: applePayIntentReplaced,
                }
            );
            // Set remount pending BEFORE clearing to prevent race conditions
            applePayRemountPendingRef.current = true;
            // Clear the existing button container before remounting
            if (applePayRef.current) {
                applePayRef.current.innerHTML = '';
            }
            // Reset mounted state immediately for cleared intents, with delay for replaced
            setApplePayMounted(false);
            setAllWalletsMounted(false);
            // Clear pending flag after a delay to allow new intent to arrive
            setTimeout(() => {
                applePayRemountPendingRef.current = false;
                console.log(
                    '[PAYMENT] Apple Pay remount sequence complete, ready for new mount'
                );
                // Increment trigger to force mount effect to re-run
                setMountTrigger((prev) => prev + 1);
            }, 100);
        }

        prevGooglePayIntentRef.current = googlePayIntent;
        prevApplePayIntentRef.current = applePayIntent;
    }, [googlePayIntent, applePayIntent, googlePayMounted, applePayMounted]);

    // Web app pattern: Notify parent when ALL expected wallet buttons are mounted (or failed)
    // This mirrors the $q.all(walletPromises) pattern that waits for all buttons
    useEffect(() => {
        // Don't check during initial loading
        if (isLoading) return;

        // Determine which wallets we SHOULD expect based on DEVICE capability AND feature flags
        // This ensures we wait for ALL expected wallets before revealing any
        // - Google Pay: Expected if feature flag is enabled
        // - Apple Pay: Expected on Apple devices IF feature flag is enabled
        const shouldExpectGooglePay = googlePayFeatureEnabled; // Only expect if feature flag enabled
        const shouldExpectApplePay = isAppleDevice && applePayFeatureEnabled; // Only expect on Apple devices AND if feature flag enabled

        // Check if all expected wallets are mounted OR failed
        // Treat failures as "ready" so we don't wait forever in mobile emulation mode
        const googlePayReady =
            !shouldExpectGooglePay || googlePayMounted || googlePayFailed;
        const applePayReady =
            !shouldExpectApplePay || applePayMounted || applePayFailed;

        console.log('[PAYMENT] Checking wallet readiness:', {
            shouldExpectGooglePay,
            shouldExpectApplePay,
            isAppleDevice,
            googlePayMounted,
            applePayMounted,
            googlePayFailed,
            applePayFailed,
            googlePayReady,
            applePayReady,
            allReady: googlePayReady && applePayReady,
        });

        // Only notify and reveal when ALL expected wallets are ready (mounted or failed)
        if (googlePayReady && applePayReady && !allWalletsMounted) {
            console.log(
                '[PAYMENT] All wallets ready (mounted or failed), revealing buttons and notifying parent'
            );
            setAllWalletsMounted(true);
            onWalletsReady?.();
        }
    }, [
        isLoading,
        isAppleDevice,
        googlePayMounted,
        applePayMounted,
        googlePayFailed,
        applePayFailed,
        allWalletsMounted,
        onWalletsReady,
        googlePayFeatureEnabled,
        applePayFeatureEnabled,
    ]);

    // Mount Google Pay button
    // Web app pattern: Check for methods before calling (like line 450-464 in web app)
    useEffect(() => {
        console.log('[PAYMENT] Google Pay mount effect check:', {
            showGooglePay,
            hasIntent: !!googlePayIntent,
            hasRef: !!googlePayRef.current,
            alreadyMounted: googlePayMounted,
            remountPending: googlePayRemountPendingRef.current,
            willMount:
                showGooglePay &&
                googlePayIntent &&
                googlePayRef.current &&
                !googlePayMounted &&
                !googlePayRemountPendingRef.current,
        });

        // Don't mount if remount is pending - wait for the remount sequence to complete
        if (googlePayRemountPendingRef.current) {
            console.log('[PAYMENT] Google Pay mount blocked - remount pending');
            return;
        }

        if (
            !showGooglePay ||
            !googlePayIntent ||
            !googlePayRef.current ||
            googlePayMounted
        ) {
            return;
        }

        const mountGooglePay = async () => {
            try {
                console.log('[PAYMENT] Mounting Google Pay button');
                const handler = googlePayHandler as any;

                // Web app pattern: Check if setPaymentIntent exists (line 450)
                if (typeof handler?.setPaymentIntent !== 'function') {
                    console.error(
                        '[PAYMENT] setPaymentIntent method not found on Google Pay handler'
                    );
                    console.error(
                        '[PAYMENT] Available methods:',
                        Object.keys(handler || {})
                    );
                    setGooglePayFailed(true);
                    return;
                }

                // Web app pattern: Check if mountPaymentButton exists (line 458)
                if (typeof handler?.mountPaymentButton !== 'function') {
                    console.error(
                        '[PAYMENT] mountPaymentButton not found. Available methods:',
                        Object.keys(handler || {})
                    );
                    setGooglePayFailed(true);
                    return;
                }

                // Extract the payment_intent object from the response wrapper
                // The API returns {success: true, payment_intent: {...}} but Chargebee expects just the payment_intent
                const intentData =
                    (googlePayIntent as any)?.payment_intent || googlePayIntent;
                console.log(
                    '[PAYMENT] Setting payment intent on Google Pay handler...'
                );
                handler.setPaymentIntent(intentData);

                // Verify container exists before mounting
                const container = document.getElementById('google-pay-button');
                if (!container) {
                    console.error(
                        '[PAYMENT] Google Pay container not found in DOM, retrying...'
                    );
                    // Retry after a short delay
                    setTimeout(() => setGooglePayMounted(false), 100);
                    return;
                }

                // Check if button already exists in container (prevent duplicates)
                if (container.children.length > 0) {
                    console.log(
                        '[PAYMENT] Google Pay button already exists in container, skipping mount'
                    );
                    setGooglePayMounted(true);
                    return;
                }

                console.log(
                    '[PAYMENT] Mounting Google Pay button with options...'
                );

                await handler.mountPaymentButton(
                    '#google-pay-button',
                    {
                        buttonType: hasTrial ? 'subscribe' : 'pay',
                        buttonColor: isDarkMode ? 'white' : 'black',
                        buttonBorderType: 'no_border',
                        buttonLocale: 'en',
                    },
                    {
                        emailRequired: true,
                        billingAddressRequired: true,
                    }
                );

                setGooglePayMounted(true);
                console.log('[PAYMENT] Google Pay button mounted successfully');

                // Add click handler and apply padding styles for payment processing
                setTimeout(() => {
                    const container =
                        document.getElementById('google-pay-button');
                    const button = container?.querySelector(
                        'button, div[role="button"], gpay-button'
                    ) as HTMLElement | null;
                    if (button) {
                        button.addEventListener('click', handleGooglePayClick);
                        // Apply padding directly to the button element for consistent sizing
                        button.style.paddingTop = '2px';
                        button.style.paddingBottom = '4px';
                        button.style.paddingLeft = '12px';
                        button.style.paddingRight = '12px';
                        // Force button to fill container width
                        button.style.width = '100%';
                        button.style.maxWidth = '100%';
                    }
                    // Also style any intermediate wrapper divs from Google Pay SDK
                    const allDivs = container?.querySelectorAll('div');
                    allDivs?.forEach((div) => {
                        (div as HTMLElement).style.width = '100%';
                        (div as HTMLElement).style.maxWidth = '100%';
                    });
                }, 100);
            } catch (err) {
                console.error('[PAYMENT] Failed to mount Google Pay:', err);
                // Mark as failed so we don't wait forever for it to mount
                setGooglePayFailed(true);
            }
        };

        mountGooglePay();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        showGooglePay,
        googlePayIntent,
        googlePayMounted,
        hasTrial,
        googlePayHandler,
        mountTrigger,
        isDarkMode,
    ]);

    // Mount Apple Pay button
    useEffect(() => {
        // Don't mount if remount is pending - wait for the remount sequence to complete
        if (applePayRemountPendingRef.current) {
            console.log('[PAYMENT] Apple Pay mount blocked - remount pending');
            return;
        }

        if (
            !showApplePay ||
            !applePayIntent ||
            !applePayRef.current ||
            applePayMounted
        ) {
            return;
        }

        const mountApplePay = async () => {
            try {
                console.log('[PAYMENT] Mounting Apple Pay button');
                const handler = applePayHandler as {
                    setPaymentIntent: (intent: unknown) => void;
                    mountPaymentButton: (
                        selector: string,
                        options: object
                    ) => Promise<void>;
                    handlePayment: () => Promise<{ token: string }>;
                };

                // Extract the payment_intent object from the response wrapper
                // The API returns {success: true, payment_intent: {...}} but Chargebee expects just the payment_intent
                const intentData =
                    (applePayIntent as any)?.payment_intent || applePayIntent;
                handler.setPaymentIntent(intentData);

                // Build recurringPaymentRequest following web app pattern
                const membershipName = planName
                    ? `${planName} Membership`
                    : 'Premium Membership';
                const managementURL =
                    typeof window !== 'undefined'
                        ? `${window.location.protocol}//${window.location.host}/plans`
                        : 'https://niio.com/plans';

                // Format trial end date for billing agreement
                const userLocale =
                    typeof navigator !== 'undefined'
                        ? navigator.language
                        : 'en-US';
                const trialEndDateFormatted = trialEndDate
                    ? trialEndDate.toLocaleDateString(userLocale, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                      })
                    : null;

                // Build billing agreement text
                let billingAgreement = `You authorize ${
                    isYearly ? 'annual' : 'monthly'
                } charges for Niio Art ${membershipName}.`;
                if (hasTrial && trialEndDateFormatted) {
                    billingAgreement = `Free trial until ${trialEndDateFormatted}, then $${amount.toFixed(
                        2
                    )} ${
                        isYearly ? 'annually' : 'monthly'
                    } for Niio Art ${membershipName}.`;
                }

                // Build recurringPaymentRequest
                const recurringPaymentRequest: Record<string, unknown> = {
                    paymentDescription: hasTrial
                        ? `Niio Art ${membershipName} - Free Trial`
                        : `Niio Art ${membershipName} Subscription`,
                    regularBilling: {
                        label: hasTrial
                            ? isYearly
                                ? 'Annual subscription after trial'
                                : 'Monthly subscription after trial'
                            : isYearly
                            ? 'Annual subscription'
                            : 'Monthly subscription',
                        amount: amount.toFixed(2),
                        paymentTiming: 'recurring',
                        recurringPaymentIntervalUnit: isYearly
                            ? 'year'
                            : 'month',
                        recurringPaymentIntervalCount: 1,
                        ...(hasTrial && trialEndDate
                            ? {
                                  recurringPaymentStartDate:
                                      trialEndDate.toISOString(),
                              }
                            : {}),
                    },
                    billingAgreement,
                    managementURL,
                };

                // Add trial billing if applicable
                if (hasTrial) {
                    recurringPaymentRequest.immediatePaymentLineItem = {
                        label: 'Due today',
                        amount: '0.00',
                        paymentTiming: 'immediate',
                    };
                    recurringPaymentRequest.trialBilling = {
                        label: `Free trial (${trialDays} days)`,
                        amount: '0.00',
                        paymentTiming: 'deferred',
                    };
                }

                // Verify container exists before mounting
                const container = document.getElementById('apple-pay-button');
                if (!container) {
                    console.error(
                        '[PAYMENT] Apple Pay container not found in DOM, retrying...'
                    );
                    // Retry after a short delay
                    setTimeout(() => setApplePayMounted(false), 100);
                    return;
                }

                // Check if button already exists in container (prevent duplicates)
                if (container.children.length > 0) {
                    console.log(
                        '[PAYMENT] Apple Pay button already exists in container, skipping mount'
                    );
                    setApplePayMounted(true);
                    return;
                }

                await handler.mountPaymentButton('#apple-pay-button', {
                    buttonColor: isDarkMode ? 'white' : 'black',
                    buttonType: 'subscribe',
                    locale: userLocale.replace('-', '_'),
                    recurringPaymentRequest,
                });

                setApplePayMounted(true);
                console.log('[PAYMENT] Apple Pay button mounted');

                // Apple Pay handles payment automatically through Chargebee
                handler
                    .handlePayment()
                    .then((result) => {
                        console.log('[PAYMENT] Apple Pay success:', result);
                        const gatewayToken = (result as any)?.paymentIntent
                            ?.active_payment_attempt?.id_at_gateway;
                        const gatewayAccountId =
                            (result as any)?.paymentIntent
                                ?.gateway_account_id || '';

                        // Extract billing info from Apple Pay result
                        // Apple Pay provides billingContact with givenName, familyName, emailAddress
                        const billingContact =
                            (result as any)?.billingContact ||
                            (result as any)?.shippingContact;
                        const billingInfo: WalletBillingInfo = {
                            firstName:
                                billingContact?.givenName ||
                                billingContact?.given_name ||
                                '',
                            lastName:
                                billingContact?.familyName ||
                                billingContact?.family_name ||
                                '',
                            email:
                                billingContact?.emailAddress ||
                                billingContact?.email ||
                                '',
                        };

                        console.log(
                            '[PAYMENT] Apple Pay billing info:',
                            billingInfo
                        );

                        if (gatewayToken) {
                            onApplePaySuccess(
                                gatewayToken,
                                gatewayAccountId,
                                billingInfo
                            );
                        }
                    })
                    .catch((error) => {
                        console.error('[PAYMENT] Apple Pay failed:', error);
                        // Don't show error for user cancellation
                        const isCancellation =
                            error?.message?.includes('cancel') ||
                            error?.message?.includes('closed') ||
                            error?.code === 'CANCELLED';
                        if (!isCancellation) {
                            onError(error?.message || 'Apple Pay failed');
                        }
                    });
            } catch (err) {
                console.error('[PAYMENT] Failed to mount Apple Pay:', err);
                // Mark as failed so we don't wait forever for it to mount
                setApplePayFailed(true);
            }
        };

        mountApplePay();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        showApplePay,
        applePayIntent,
        applePayMounted,
        applePayHandler,
        onApplePaySuccess,
        onError,
        hasTrial,
        trialDays,
        trialEndDate,
        amount,
        isYearly,
        planName,
        mountTrigger,
        isDarkMode,
    ]);

    // Handle Google Pay click
    const handleGooglePayClick = useCallback(async () => {
        if (!googlePayHandler || walletPaymentInProgress) return;

        setWalletPaymentInProgress(true);
        let paymentCompleted = false;
        let verificationInProgress = false;

        try {
            console.log('[PAYMENT] Processing Google Pay');
            const handler = googlePayHandler as {
                handlePayment: () => Promise<{
                    token?: string;
                    paymentIntent?: {
                        active_payment_attempt?: {
                            id_at_gateway?: string;
                        };
                    };
                }>;
            };

            // Handle visibility change to detect when user returns from payment sheet
            // If SDK callback doesn't fire within a reasonable time, verify with backend
            const handleVisibilityChange = async () => {
                if (
                    document.visibilityState === 'visible' &&
                    walletPaymentInProgress &&
                    !paymentCompleted &&
                    !verificationInProgress
                ) {
                    // User returned to the page, wait a bit for SDK callback
                    console.log(
                        '[PAYMENT] User returned to page, waiting for SDK callback...'
                    );

                    // Wait 5 seconds for SDK callback before verifying
                    setTimeout(async () => {
                        if (
                            walletPaymentInProgress &&
                            !paymentCompleted &&
                            !verificationInProgress
                        ) {
                            console.log(
                                '[PAYMENT] SDK callback not received after return - may be blocked by browser extensions'
                            );

                            // If we have a verification callback, use it to check payment status
                            if (onGooglePayTimeout) {
                                verificationInProgress = true;
                                console.log(
                                    '[PAYMENT] Verifying payment status with backend...'
                                );
                                try {
                                    const paymentSucceeded =
                                        await onGooglePayTimeout();
                                    if (paymentSucceeded) {
                                        console.log(
                                            '[PAYMENT] Backend verified payment succeeded despite SDK hang'
                                        );
                                        paymentCompleted = true;
                                        // Success is handled by the timeout callback
                                    } else {
                                        console.log(
                                            '[PAYMENT] Backend verification: payment not found'
                                        );
                                        // Don't show error - user may have cancelled
                                    }
                                } catch (verifyErr) {
                                    console.error(
                                        '[PAYMENT] Verification failed:',
                                        verifyErr
                                    );
                                }
                                verificationInProgress = false;
                                setWalletPaymentInProgress(false);
                            } else {
                                // No verification available, just reset state
                                // User can retry or use card payment
                                setWalletPaymentInProgress(false);
                            }
                        }
                    }, 5000);
                }
            };
            document.addEventListener(
                'visibilitychange',
                handleVisibilityChange
            );

            const result = await handler.handlePayment();
            paymentCompleted = true;
            console.log('[PAYMENT] Google Pay success:', result);

            const gatewayToken =
                result?.paymentIntent?.active_payment_attempt?.id_at_gateway;
            const gatewayAccountId =
                (result?.paymentIntent as any)?.gateway_account_id || '';

            // Extract billing info from Google Pay result
            // Google Pay provides payerName, payerEmail in the result
            const billingInfo: WalletBillingInfo = {
                firstName: '',
                lastName: '',
                email: (result as any)?.payerEmail || '',
            };

            // Google Pay may provide payerName as full name, try to split it
            const payerName = (result as any)?.payerName || '';
            if (payerName) {
                const nameParts = payerName.trim().split(/\s+/);
                billingInfo.firstName = nameParts[0] || '';
                billingInfo.lastName = nameParts.slice(1).join(' ') || '';
            }

            console.log('[PAYMENT] Google Pay billing info:', billingInfo);

            if (gatewayToken) {
                onGooglePaySuccess(gatewayToken, gatewayAccountId, billingInfo);
            }

            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            );
        } catch (err: any) {
            console.error('[PAYMENT] Google Pay failed:', err);

            // Don't show error for user cancellation
            const isCancellation =
                err?.message?.includes('cancel') ||
                err?.message?.includes('closed') ||
                err?.message === 'User closed the Payment Request UI.' ||
                err?.code === 'CANCELLED';
            if (!isCancellation) {
                onError(err?.message || 'Google Pay failed');
            }
        } finally {
            setWalletPaymentInProgress(false);
        }
    }, [
        googlePayHandler,
        walletPaymentInProgress,
        onGooglePaySuccess,
        onError,
        onGooglePayTimeout,
    ]);

    // Detect card brand based on card number prefix
    const detectCardBrand = (number: string): string | null => {
        const cleanNumber = number.replace(/\D/g, '');
        if (!cleanNumber) return null;

        // Visa: starts with 4
        if (/^4/.test(cleanNumber)) return 'visa';
        // Mastercard: starts with 51-55 or 2221-2720
        if (/^5[1-5]/.test(cleanNumber) || /^2[2-7]/.test(cleanNumber))
            return 'mastercard';
        // American Express: starts with 34 or 37
        if (/^3[47]/.test(cleanNumber)) return 'amex';
        // Discover: starts with 6011, 622126-622925, 644-649, 65
        if (
            /^6011/.test(cleanNumber) ||
            /^65/.test(cleanNumber) ||
            /^64[4-9]/.test(cleanNumber)
        )
            return 'discover';

        return null;
    };

    // Format card number with spaces (Amex uses 4-6-5 pattern, others use 4-4-4-4)
    const formatCardNumber = (value: string) => {
        const digits = value.replace(/\D/g, '');
        const brand = detectCardBrand(digits);
        setCardBrand(brand);

        // Amex has 15 digits with 4-6-5 pattern
        if (brand === 'amex') {
            const truncated = digits.slice(0, 15);
            if (truncated.length <= 4) return truncated;
            if (truncated.length <= 10)
                return `${truncated.slice(0, 4)} ${truncated.slice(4)}`;
            return `${truncated.slice(0, 4)} ${truncated.slice(
                4,
                10
            )} ${truncated.slice(10)}`;
        }

        // Other cards: 16 digits with 4-4-4-4 pattern
        const truncated = digits.slice(0, 16);
        const groups = truncated.match(/.{1,4}/g) || [];
        return groups.join(' ');
    };

    // Format expiry date - handles backspace properly
    const formatExpiryDate = (value: string, previousValue: string) => {
        // If user is deleting (new value is shorter), just strip non-digits and reformat
        const digits = value.replace(/\D/g, '').slice(0, 4);

        // If we have 2+ digits, add the slash
        if (digits.length >= 2) {
            return `${digits.slice(0, 2)}/${digits.slice(2)}`;
        }
        return digits;
    };

    // Validate card data
    const validateCard = (): boolean => {
        const errors: { number?: string; expiry?: string; cvv?: string } = {};
        const cleanNumber = cardNumber.replace(/\s/g, '');

        if (!cleanNumber || cleanNumber.length < 13) {
            errors.number = 'Enter a valid card number';
        }

        const [month, year] = expiryDate.split('/');
        if (!month || !year || parseInt(month) < 1 || parseInt(month) > 12) {
            errors.expiry = 'Enter valid expiry date';
        }

        if (!cvv || cvv.length < 3) {
            errors.cvv = 'Enter valid CVV';
        }

        setCardErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle card form submit
    const handleCardSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateCard()) return;

        const [month, year] = expiryDate.split('/');
        onCardSubmit({
            number: cardNumber.replace(/\s/g, ''),
            expiryMonth: month,
            expiryYear: `20${year}`,
            cvv,
        });
    };

    // Unified loading state: either isLoading OR wallets not ready yet
    // BUT: Don't show loading if wallets are disabled by feature flags (nothing to wait for)
    const walletsDisabledByFeatureFlags = !showGooglePay && !showApplePay;
    const showLoadingSpinner =
        isLoading ||
        (!allWalletsMounted &&
            !showCardForm &&
            showDigitalWallets &&
            !walletsDisabledByFeatureFlags);
    const loadingMessage = isLoading
        ? 'Loading payment options...'
        : 'Preparing payment options...';

    return (
        <div className="space-y-4">
            {/* Digital Wallet Button Containers - ALWAYS render refs in DOM for mounting
                CRITICAL: These containers MUST exist in DOM before mount effects run
                We render them unconditionally when amount > 0, hidden with CSS
                The mount effects will run when handlers/intents arrive, and refs will be ready
                BUT: Skip entirely if wallets are disabled by feature flags (no UI to render) */}
            {showDigitalWallets && !walletsDisabledByFeatureFlags && (
                <>
                    {/* Wallet Buttons Container - side-by-side when both exist, centered at 50% when single
                        Use absolute positioning when not mounted to keep refs in DOM but out of layout flow */}
                    <div
                        className={`flex gap-3 w-full ${
                            showCardForm
                                ? 'hidden'
                                : !allWalletsMounted
                                ? 'absolute opacity-0 pointer-events-none'
                                : ''
                        } ${
                            hasWorkingApplePay &&
                            hasWorkingGooglePay &&
                            allWalletsMounted
                                ? 'flex-row items-stretch'
                                : 'flex-row justify-center'
                        }`}
                    >
                        {/* Apple Pay Button (Apple devices - primary)
                            Always render on Apple devices so ref is available when handler loads */}
                        {isAppleDevice && !applePayFailed && (
                            <div
                                ref={applePayRef}
                                id="apple-pay-button"
                                className={`transition-opacity duration-200 [&>button]:!h-full [&>div]:!h-full [&>button]:!w-full [&>div]:!w-full ${
                                    !showApplePay || !allWalletsMounted
                                        ? 'opacity-0 h-0 overflow-hidden'
                                        : hasWorkingApplePay &&
                                          hasWorkingGooglePay &&
                                          allWalletsMounted
                                        ? 'flex-1 h-[48px] overflow-hidden opacity-100'
                                        : 'w-1/2 h-[48px] overflow-hidden opacity-100'
                                }`}
                            />
                        )}

                        {/* Google Pay Button - Always render so ref is available when handler loads */}
                        {!googlePayFailed && (
                            <div
                                ref={googlePayRef}
                                id="google-pay-button"
                                className={`transition-opacity duration-200 [&>button]:!h-full [&>div]:!h-full [&>button]:!w-full [&>div]:!w-full ${
                                    !showGooglePay || !allWalletsMounted
                                        ? 'opacity-0 h-0 overflow-hidden'
                                        : hasWorkingApplePay &&
                                          hasWorkingGooglePay &&
                                          allWalletsMounted
                                        ? 'flex-1 h-[48px] overflow-hidden opacity-100'
                                        : 'w-1/2 h-[48px] overflow-hidden opacity-100'
                                }`}
                            />
                        )}
                    </div>

                    {/* Unified loading spinner - single location for all loading states */}
                    {showLoadingSpinner && (
                        <div className="flex items-center justify-center h-[48px]">
                            <svg
                                className={`animate-spin h-6 w-6 mr-3 ${
                                    isDarkMode
                                        ? 'text-purple-400'
                                        : 'text-purple-600'
                                }`}
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                            <span
                                className={`text-sm ${
                                    isDarkMode
                                        ? 'text-gray-400'
                                        : 'text-gray-600'
                                }`}
                            >
                                {loadingMessage}
                            </span>
                        </div>
                    )}

                    {/* Processing indicator - only show when wallets mounted and not in card form */}
                    {walletPaymentInProgress &&
                        !showCardForm &&
                        allWalletsMounted && (
                            <div className="flex items-center justify-center py-2">
                                <svg
                                    className={`animate-spin h-5 w-5 mr-2 ${
                                        isDarkMode
                                            ? 'text-purple-400'
                                            : 'text-purple-600'
                                    }`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                                <span
                                    className={`text-sm ${
                                        isDarkMode
                                            ? 'text-gray-400'
                                            : 'text-gray-600'
                                    }`}
                                >
                                    Processing payment...
                                </span>
                            </div>
                        )}

                    {/* Card alternative link - only show when wallets mounted and not in card form */}
                    {hasWallet && !showCardForm && allWalletsMounted && (
                        <button
                            type="button"
                            onClick={() => setShowCardForm(true)}
                            className={`w-full text-center text-sm py-2 transition-colors ${
                                isDarkMode
                                    ? 'text-gray-400 hover:text-purple-400'
                                    : 'text-gray-500 hover:text-purple-600'
                            }`}
                        >
                            Pay with card instead
                        </button>
                    )}
                </>
            )}

            {/* Credit Card Form - Hidden by default, shown on request or if no wallet available */}
            {/* Wait for walletsInitialized to prevent UI flicker */}
            {!isLoading &&
                walletsInitialized &&
                (showCardForm || !hasWallet || !showDigitalWallets) && (
                    <>
                        <form onSubmit={handleCardSubmit} className="space-y-3">
                            {/* Credit Card Visual Container */}
                            <div
                                className={`border rounded-lg p-4 shadow-sm ${
                                    isDarkMode
                                        ? 'border-gray-700 bg-gradient-to-br from-gray-800 to-gray-900'
                                        : 'border-gray-200 bg-gradient-to-br from-gray-50 to-white'
                                }`}
                            >
                                {/* Accepted Cards Row */}
                                <div className="flex items-center justify-between mb-3">
                                    <span
                                        className={`text-xs font-medium ${
                                            isDarkMode
                                                ? 'text-gray-400'
                                                : 'text-gray-500'
                                        }`}
                                    >
                                        Accepted Cards
                                    </span>
                                    <div className="flex items-center gap-1.5">
                                        {/* Visa */}
                                        <div
                                            className={`h-6 px-1.5 rounded flex items-center justify-center transition-opacity ${
                                                cardBrand === 'visa'
                                                    ? 'opacity-100'
                                                    : cardBrand
                                                    ? 'opacity-30'
                                                    : 'opacity-70'
                                            }`}
                                            style={{
                                                backgroundColor: '#1A1F71',
                                            }}
                                        >
                                            <span className="text-white text-[9px] font-bold tracking-tight">
                                                VISA
                                            </span>
                                        </div>
                                        {/* Mastercard */}
                                        <div
                                            className={`h-6 w-9 rounded flex items-center justify-center transition-opacity ${
                                                cardBrand === 'mastercard'
                                                    ? 'opacity-100'
                                                    : cardBrand
                                                    ? 'opacity-30'
                                                    : 'opacity-70'
                                            }`}
                                            style={{
                                                backgroundColor: '#f5f5f5',
                                            }}
                                        >
                                            <svg
                                                className="h-4 w-auto"
                                                viewBox="0 0 32 20"
                                                fill="none"
                                            >
                                                <circle
                                                    cx="10"
                                                    cy="10"
                                                    r="8"
                                                    fill="#EB001B"
                                                />
                                                <circle
                                                    cx="22"
                                                    cy="10"
                                                    r="8"
                                                    fill="#F79E1B"
                                                />
                                                <path
                                                    d="M16 4.5A8 8 0 0 0 12 10a8 8 0 0 0 4 5.5A8 8 0 0 0 20 10a8 8 0 0 0-4-5.5Z"
                                                    fill="#FF5F00"
                                                />
                                            </svg>
                                        </div>
                                        {/* American Express */}
                                        <div
                                            className={`h-6 px-1.5 rounded flex items-center justify-center transition-opacity ${
                                                cardBrand === 'amex'
                                                    ? 'opacity-100'
                                                    : cardBrand
                                                    ? 'opacity-30'
                                                    : 'opacity-70'
                                            }`}
                                            style={{
                                                backgroundColor: '#006FCF',
                                            }}
                                        >
                                            <span className="text-white text-[9px] font-bold tracking-tight">
                                                AMEX
                                            </span>
                                        </div>
                                        {/* Discover */}
                                        <div
                                            className={`h-6 px-1 rounded flex items-center justify-center transition-opacity ${
                                                cardBrand === 'discover'
                                                    ? 'opacity-100'
                                                    : cardBrand
                                                    ? 'opacity-30'
                                                    : 'opacity-70'
                                            }`}
                                            style={{
                                                backgroundColor: '#FF6000',
                                            }}
                                        >
                                            <span className="text-white text-[8px] font-bold tracking-tight">
                                                DISC
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Card Number Input with Icon */}
                                <div className="relative">
                                    <div
                                        className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                                            isDarkMode
                                                ? 'text-gray-500'
                                                : 'text-gray-400'
                                        }`}
                                    >
                                        {/* Credit card icon */}
                                        <svg
                                            className="w-5 h-5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={1.5}
                                                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                                            />
                                        </svg>
                                    </div>
                                    <input
                                        id="card-number"
                                        type="text"
                                        value={cardNumber}
                                        onChange={(e) =>
                                            setCardNumber(
                                                formatCardNumber(e.target.value)
                                            )
                                        }
                                        placeholder="Card number *"
                                        data-ph-no-capture
                                        className={`ph-no-capture w-full pl-10 pr-12 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono tracking-wider ${
                                            cardErrors.number
                                                ? isDarkMode
                                                    ? 'border-red-700 bg-red-900/20 text-white placeholder-gray-500'
                                                    : 'border-red-300 bg-red-50 text-gray-900 placeholder-gray-400'
                                                : isDarkMode
                                                ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-500'
                                                : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400'
                                        }`}
                                        autoComplete="cc-number"
                                        inputMode="numeric"
                                    />
                                    {/* Dynamic card brand indicator */}
                                    {cardBrand && (
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                            {cardBrand === 'visa' && (
                                                <div
                                                    className="h-5 px-1.5 rounded flex items-center justify-center"
                                                    style={{
                                                        backgroundColor:
                                                            '#1A1F71',
                                                    }}
                                                >
                                                    <span className="text-white text-[9px] font-bold tracking-tight">
                                                        VISA
                                                    </span>
                                                </div>
                                            )}
                                            {cardBrand === 'mastercard' && (
                                                <svg
                                                    className="h-5 w-auto"
                                                    viewBox="0 0 32 20"
                                                    fill="none"
                                                >
                                                    <circle
                                                        cx="10"
                                                        cy="10"
                                                        r="8"
                                                        fill="#EB001B"
                                                    />
                                                    <circle
                                                        cx="22"
                                                        cy="10"
                                                        r="8"
                                                        fill="#F79E1B"
                                                    />
                                                    <path
                                                        d="M16 4.5A8 8 0 0 0 12 10a8 8 0 0 0 4 5.5A8 8 0 0 0 20 10a8 8 0 0 0-4-5.5Z"
                                                        fill="#FF5F00"
                                                    />
                                                </svg>
                                            )}
                                            {cardBrand === 'amex' && (
                                                <div
                                                    className="h-5 px-1.5 rounded flex items-center justify-center"
                                                    style={{
                                                        backgroundColor:
                                                            '#006FCF',
                                                    }}
                                                >
                                                    <span className="text-white text-[9px] font-bold tracking-tight">
                                                        AMEX
                                                    </span>
                                                </div>
                                            )}
                                            {cardBrand === 'discover' && (
                                                <div
                                                    className="h-5 px-1 rounded flex items-center justify-center"
                                                    style={{
                                                        backgroundColor:
                                                            '#FF6000',
                                                    }}
                                                >
                                                    <span className="text-white text-[8px] font-bold tracking-tight">
                                                        DISC
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {cardErrors.number && (
                                    <p
                                        className={`mt-1 text-xs ${
                                            isDarkMode
                                                ? 'text-red-400'
                                                : 'text-red-600'
                                        }`}
                                    >
                                        {cardErrors.number}
                                    </p>
                                )}

                                {/* Expiry and CVV Row */}
                                <div className="grid grid-cols-2 gap-3 mt-3">
                                    <div className="relative">
                                        <div
                                            className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                                                isDarkMode
                                                    ? 'text-gray-500'
                                                    : 'text-gray-400'
                                            }`}
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={1.5}
                                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                />
                                            </svg>
                                        </div>
                                        <input
                                            id="expiry"
                                            type="text"
                                            value={expiryDate}
                                            onChange={(e) =>
                                                setExpiryDate(
                                                    formatExpiryDate(
                                                        e.target.value,
                                                        expiryDate
                                                    )
                                                )
                                            }
                                            placeholder="MM/YY *"
                                            data-ph-no-capture
                                            className={`ph-no-capture w-full pl-9 pr-3 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono ${
                                                cardErrors.expiry
                                                    ? isDarkMode
                                                        ? 'border-red-700 bg-red-900/20 text-white placeholder-gray-500'
                                                        : 'border-red-300 bg-red-50 text-gray-900 placeholder-gray-400'
                                                    : isDarkMode
                                                    ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-500'
                                                    : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400'
                                            }`}
                                            autoComplete="cc-exp"
                                            inputMode="numeric"
                                        />
                                        {cardErrors.expiry && (
                                            <p
                                                className={`mt-1 text-xs ${
                                                    isDarkMode
                                                        ? 'text-red-400'
                                                        : 'text-red-600'
                                                }`}
                                            >
                                                {cardErrors.expiry}
                                            </p>
                                        )}
                                    </div>
                                    <div className="relative">
                                        <div
                                            className={`absolute left-3 top-1/2 -translate-y-1/2 ${
                                                isDarkMode
                                                    ? 'text-gray-500'
                                                    : 'text-gray-400'
                                            }`}
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={1.5}
                                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                                />
                                            </svg>
                                        </div>
                                        <input
                                            id="cvv"
                                            type="text"
                                            value={cvv}
                                            onChange={(e) =>
                                                setCvv(
                                                    e.target.value
                                                        .replace(/\D/g, '')
                                                        .slice(0, 4)
                                                )
                                            }
                                            placeholder="CVV *"
                                            data-ph-no-capture
                                            className={`ph-no-capture w-full pl-9 pr-3 py-2.5 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono ${
                                                cardErrors.cvv
                                                    ? isDarkMode
                                                        ? 'border-red-700 bg-red-900/20 text-white placeholder-gray-500'
                                                        : 'border-red-300 bg-red-50 text-gray-900 placeholder-gray-400'
                                                    : isDarkMode
                                                    ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-500'
                                                    : 'border-gray-300 bg-white text-gray-900 placeholder-gray-400'
                                            }`}
                                            autoComplete="cc-csc"
                                            inputMode="numeric"
                                        />
                                        {cardErrors.cvv && (
                                            <p
                                                className={`mt-1 text-xs ${
                                                    isDarkMode
                                                        ? 'text-red-400'
                                                        : 'text-red-600'
                                                }`}
                                            >
                                                {cardErrors.cvv}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Submit Button - disabled until all required fields are filled */}
                            <button
                                type="submit"
                                disabled={
                                    walletPaymentInProgress ||
                                    !cardNumber ||
                                    !expiryDate ||
                                    !cvv ||
                                    !isUserInfoComplete
                                }
                                className="w-full py-2.5 px-4 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {hasTrial
                                    ? 'Start Free Trial'
                                    : 'Subscribe Now'}
                            </button>
                        </form>

                        {/* Back to wallet link */}
                        {hasWallet && showCardForm && showDigitalWallets && (
                            <button
                                type="button"
                                onClick={() => setShowCardForm(false)}
                                className={`w-full text-center text-sm py-1 transition-colors ${
                                    isDarkMode
                                        ? 'text-gray-400 hover:text-purple-400'
                                        : 'text-gray-500 hover:text-purple-600'
                                }`}
                            >
                                ← Back to{' '}
                                {showApplePay && showGooglePay
                                    ? 'digital wallets'
                                    : showApplePay
                                    ? 'Apple Pay'
                                    : 'Google Pay'}
                            </button>
                        )}
                    </>
                )}

            {/* Security Note */}
            <p
                className={`text-xs text-center flex items-center justify-center space-x-1 ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-500'
                }`}
            >
                <svg
                    className={`w-4 h-4 ${
                        isDarkMode ? 'text-green-400' : 'text-green-500'
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                </svg>
                <span>Secure payment powered by Chargebee</span>
            </p>
        </div>
    );
}

export default PaymentMethodsSection;
